import { For, location, onCleanup, onMount, reactive } from 'cdui-js';
import i18n from '../../../i18n';
import { fetchStockTransferOutBrokers, type StockTransferOutBroker } from '../../../api/stockTransferIn';
import { showToast } from '../components/Toast';
import { goBackByBrowser, routeReplace } from '../../../lib/browser';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import { IconSearch } from './icons';
import { StockTransferNav } from './Nav';
import {
  brokerDisplayName,
  clearOtherOutBrokerDraft,
  readMarketQuery,
  setSelectedOutBroker,
  syncStockTransferAppShell,
  withStockTransferQuery,
  type StockTransferMarket,
} from './shared';
import './page.css';

export const StockTransferInSelectBroker = () => {
  const state = reactive({
    market: 'HK' as StockTransferMarket,
    keyword: '',
    loading: false,
    brokers: [] as StockTransferOutBroker[],
  });

  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  let searchSeq = 0;

  const loadBrokers = async (keyword?: string) => {
    const seq = ++searchSeq;
    state.loading = true;
    try {
      const list = await fetchStockTransferOutBrokers(keyword?.trim() || undefined);
      if (seq !== searchSeq) return;
      state.brokers = list;
    } catch {
      if (seq !== searchSeq) return;
      showToast(i18n.StockTransferIn.LoadFail);
    } finally {
      if (seq === searchSeq) state.loading = false;
    }
  };

  const scheduleSearch = (keyword: string) => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      void loadBrokers(keyword);
    }, 300);
  };

  const onPick = (item: StockTransferOutBroker) => {
    setSelectedOutBroker(item);
    const brokerId = String(item.id);
    clearOtherOutBrokerDraft();
    if (item.isOther === 1) {
      location.routeTo(
        withStockTransferQuery('/h5/stock-transfer-in/other-broker', {
          market: state.market,
          brokerId,
        }),
        [0, 0],
      );
      return;
    }
    location.routeTo(
      withStockTransferQuery('/h5/stock-transfer-in/broker', {
        market: state.market,
        brokerId,
      }),
      [0, 0],
    );
  };

  const onClose = () => {
    routeReplace(withStockTransferQuery('/h5/stock-transfer-in'));
  };

  const rowSubtitle = (item: StockTransferOutBroker) => {
    if (item.isOther === 1) return '';
    return item.brokerCode?.trim() || '';
  };

  onMount(() => {
    syncStockTransferAppShell();
    state.market = readMarketQuery();
    void (async () => {
      try {
        await bootDepositApp();
      } catch {
        // 浏览器调试
      }
      await loadBrokers('');
    })();
    onCleanup(() => {
      if (searchTimer) clearTimeout(searchTimer);
    });
  });

  return (
    <div class="stock-transfer-in-h5 sti-select-broker-page">
      <StockTransferNav title={i18n.StockTransferIn.SelectOutBroker} onBack={goBackByBrowser} onClose={onClose} />
      <div class="sti-search-row">
        <div class="sti-search-bar">
          <IconSearch class="sti-search-icon" />
          <input
            class="sti-search-input"
            placeholder={i18n.StockTransferIn.SearchBrokerPlaceholder}
            value={state.keyword}
            oninput={(event) => {
              const value = (event.target as HTMLInputElement).value;
              state.keyword = value;
              scheduleSearch(value);
            }}
          />
        </div>
        <button type="button" class="sti-search-cancel" onclick={goBackByBrowser}>
          {i18n.Common.Cancel}
        </button>
      </div>
      <div class="sti-broker-list">
        <For each={state.brokers}>
          {(item) => {
            const sub = rowSubtitle(item);
            return (
              <div class="sti-broker-list-item" onclick={() => onPick(item)}>
                <div class="sti-broker-list-name">{brokerDisplayName(item)}</div>
                {sub ? <div class="sti-broker-list-sub">{sub}</div> : null}
              </div>
            );
          }}
        </For>
        {!state.loading && !state.brokers.length ? (
          <div class="sti-broker-list-empty color-L font-s-S">{i18n.Common.NoData}</div>
        ) : null}
      </div>
    </div>
  );
};
